import React, { useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FaExchangeAlt } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import MultiSelectDropdown from '../layout/MultiSelectDropdown1';
import {
  isLeadFieldExcludedFromMapping,
  isStandardConversionSkipsLeadNameMapping,
} from './leadFieldMappingUtils';

const FieldMappingManager = ({
  show,
  onClose,
  variant = 'modal',
  allowMigrationConfig = true,
  redirectPreference,
  setRedirectPreference,
  enabledMappingObjects,
  onMappingObjectSelection,
  loadingStageOptions,
  defaultOpportunityStage,
  setDefaultOpportunityStage,
  opportunityStageOptions,
  loadingMappingFields,
  fields,
  objectIds,
  mappingTargets,
  isMappingCompleted,
  getCurrentMapping,
  handleMappingChange,
  getAvailableFields,
  saveFieldMappings,
  savingMappings,
  dontMarkConverted = false,
  setDontMarkConverted,
  leadConvertStatusSelectKey = '',
  setLeadConvertStatusSelectKey,
  leadStatusPicklistForConvertHide = []
}) => {
  const [showMigrationConfigModal, setShowMigrationConfigModal] = useState(false);
  const [migrationConfigMappings, setMigrationConfigMappings] = useState({});
  const [migrationConfigLoading, setMigrationConfigLoading] = useState(false);
  const [migrationConfigError, setMigrationConfigError] = useState('');
  const [migrationTargetConstraints, setMigrationTargetConstraints] = useState({});

  const childObjectOptions = [
    { key: 'activity', label: 'Activity' },
    { key: 'attachment', label: 'Attachment' },
    { key: 'quote', label: 'Quote' }
  ];
  const targetObjects = useMemo(
    () => (Array.isArray(mappingTargets) ? mappingTargets : []),
    [mappingTargets]
  );
  const picklistValues = useMemo(
    () => targetObjects.map(target => ({ key: target.key, value: target.label || target.key })),
    [targetObjects]
  );
  const selectedValues = useMemo(
    () => targetObjects.filter(target => enabledMappingObjects?.[target.key]).map(target => target.key),
    [targetObjects, enabledMappingObjects]
  );
  const enabledTargets = targetObjects.filter(target => selectedValues.includes(target.key));
  const hasEnabledTargets = enabledTargets.length > 0;
  const redirectOptions = useMemo(
    () => enabledTargets.map(target => ({ value: target.key, label: target.label || target.key })),
    [enabledTargets]
  );
  const migrationTargetOptions = useMemo(() => {
    const filteredTargets = enabledTargets.filter(target => target.isCore || target.isRelated);
    const candidates = filteredTargets.length > 0 ? filteredTargets : enabledTargets;
    return candidates.map(target => ({ value: target.key, label: target.label || target.key }));
  }, [enabledTargets]);
  const defaultMigrationTarget = migrationTargetOptions[0]?.value || 'skip';

  const buildDefaultChildMappings = (overrides = {}) => ({
    activity: '',
    attachment: '',
    quote: '',
    ...overrides
  });

  const resolveAllowedTargets = (childKey) => {
    if (['activity', 'attachment'].includes(childKey)) {
      return migrationTargetOptions;
    }
    if (childKey === 'quote') {
      return migrationTargetOptions.filter(option => option.value === 'opportunity');
    }
    const constrainedKeys = migrationTargetConstraints[childKey];
    if (!Array.isArray(constrainedKeys) || constrainedKeys.length === 0) {
      return [];
    }
    return migrationTargetOptions.filter(option => constrainedKeys.includes(option.value));
  };


  const handleMigrationConfigChange = (childKey, targetKey) => {
    setMigrationConfigMappings(prev => ({
      ...prev,
      [childKey]: targetKey === 'skip' ? '' : targetKey
    }));
  };

  const resolveMigrationMappingValue = (childKey) => {
    const currentValue = migrationConfigMappings[childKey];
    if (currentValue === '') {
      return 'skip';
    }
    return currentValue !== undefined ? currentValue : 'skip';
  };

  const handleOpenMigrationConfig = async () => {
    if (!allowMigrationConfig) {
      return;
    }
    setMigrationConfigError('');
    setMigrationConfigLoading(true);
    try {
      const configApi = new DataApi('objectconfig');
      const configResponse = await configApi.get('/lead/conversion-config');
      const leadConfig = configResponse?.data?.success ? (configResponse.data.data || {}) : {};
      const baseMappings = buildDefaultChildMappings(leadConfig.childObjectMappings || {});
      const enabledTargetObjects = enabledTargets.filter(target => target.objectName);
      if (enabledTargetObjects.length > 0) {
        const enabledTargetMap = enabledTargetObjects.reduce((acc, target) => {
          acc[target.objectName.toLowerCase()] = target.key;
          return acc;
        }, {});
        const relationshipMap = {};
        await Promise.all(childObjectOptions.map(async (child) => {
          if (['activity', 'attachment'].includes(child.key)) {
            return;
          }
          try {
            const api = new DataApi(`relatedlist/target/${child.key}`);
            const response = await api.fetchAll();
            if (response?.data?.success) {
              (response.data.data || []).forEach((item) => {
                const parentName = item?.source_object_name;
                if (!parentName) {
                  return;
                }
                const parentKey = enabledTargetMap[parentName.toLowerCase()];
                if (!parentKey) {
                  return;
                }
                if (!relationshipMap[child.key]) {
                  relationshipMap[child.key] = new Set();
                }
                relationshipMap[child.key].add(parentKey);
              });
            }
          } catch (error) {
            console.warn('Failed to load related list config for', child.key, error);
          }
        }));
        const normalized = Object.entries(relationshipMap).reduce((acc, [key, value]) => {
          acc[key] = Array.from(value);
          return acc;
        }, {});
        setMigrationTargetConstraints(normalized);
        const sanitizedMappings = childObjectOptions.reduce((acc, child) => {
          const currentValue = baseMappings[child.key];
          if (currentValue === '') {
            acc[child.key] = '';
            return acc;
          }
          if (['activity', 'attachment'].includes(child.key)) {
            acc[child.key] = migrationTargetOptions.some(option => option.value === currentValue)
              ? currentValue
              : defaultMigrationTarget;
            return acc;
          }
          const allowedKeys = normalized[child.key] || [];
          if (allowedKeys.length === 0) {
            if (child.key === 'quote') {
              acc[child.key] = migrationTargetOptions.some(option => option.value === currentValue)
                ? currentValue
                : defaultMigrationTarget;
            } else {
              acc[child.key] = '';
            }
            return acc;
          }
          acc[child.key] = allowedKeys.includes(currentValue)
            ? currentValue
            : allowedKeys[0];
          return acc;
        }, {});
        setMigrationConfigMappings(sanitizedMappings);
      } else {
        setMigrationTargetConstraints({});
        setMigrationConfigMappings(baseMappings);
      }
      setShowMigrationConfigModal(true);
    } catch (configError) {
      setMigrationConfigError('Failed to load migration config');
    } finally {
      setMigrationConfigLoading(false);
    }
  };

  const handleSaveMigrationConfig = async () => {
    try {
      setMigrationConfigLoading(true);
      setMigrationConfigError('');
      const sanitizedMappings = childObjectOptions.reduce((acc, child) => {
        const desiredTarget = migrationConfigMappings[child.key];
        if (!desiredTarget || desiredTarget === 'skip') {
          acc[child.key] = '';
          return acc;
        }
        const allowedTargets = resolveAllowedTargets(child.key).map(option => option.value);
        if (allowedTargets.length === 0) {
          acc[child.key] = '';
          return acc;
        }
        acc[child.key] = allowedTargets.includes(desiredTarget)
          ? desiredTarget
          : allowedTargets[0];
        return acc;
      }, {});
      const configApi = new DataApi('objectconfig');
      await configApi.post('/lead/conversion-config', {
        childObjectMappings: sanitizedMappings
      });
      setShowMigrationConfigModal(false);
    } catch (configError) {
      setMigrationConfigError(configError?.response?.data?.message || 'Failed to save migration config');
    } finally {
      setMigrationConfigLoading(false);
    }
  };

  const headerContent = (
    <>
      <FaExchangeAlt className="me-2" />
      Lead Field Mapping
    </>
  );

  const bodyContent = (
    <>
      {/* Lead Conversion Configuration Section */}
      <div className="mb-4 p-3 border rounded bg-light">
        <h6 className="mb-3">Lead Conversion Configuration</h6>

        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Objects to map:</Form.Label>
              <MultiSelectDropdown
                selectedValues={selectedValues}
                picklistValues={picklistValues}
                onChange={onMappingObjectSelection}
                placeholder="Select objects"
              />
              <Form.Text className="text-muted">
                Only objects allowed by your company subscription are listed here. Select the ones to use for lead
                conversion; your choices are saved on the lead object and drive the convert-lead UI (unselected objects are
                skipped).
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>After converting a lead, redirect to:</Form.Label>
              <Form.Select
                value={redirectPreference}
                onChange={(e) => setRedirectPreference(e.target.value)}
                disabled={redirectOptions.length === 0}
              >
                {redirectOptions.length === 0 ? (
                  <option value="">No objects available</option>
                ) : (
                  redirectOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                )}
              </Form.Select>
              <Form.Text className="text-muted">
                Select where users should be redirected after successfully converting a lead.
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Default Opportunity Stage (Optional):</Form.Label>
              {loadingStageOptions ? (
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="text-muted">Loading stage options...</span>
                </div>
              ) : (
                <Form.Select
                  value={defaultOpportunityStage}
                  onChange={(e) => setDefaultOpportunityStage(e.target.value)}
                >
                  <option value="">-- Select Stage (Optional) --</option>
                  {opportunityStageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              )}
              <Form.Text className="text-muted">
                Select the default stage for newly created opportunities during lead conversion.
                Leave empty to use the opportunity's default stage.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col md={12}>
            <Form.Group>
              <Form.Check
                type="checkbox"
                id="dont-mark-converted"
                label="Don't mark Lead as Converted"
                checked={!!dontMarkConverted}
                onChange={(e) =>
                  typeof setDontMarkConverted === 'function' &&
                  setDontMarkConverted(e.target.checked)
                }
              />
              <Form.Text className="text-muted">
                When enabled, clicking <em>Convert Lead</em> will not set <code>is_converted = true</code> on the lead, so the
                lead remains open. The Lead <code>id</code> field also becomes available below so you can map it to a lookup
                field on the target object (e.g. an "Original Lead" lookup on Opportunity).
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Lead Convert Status</Form.Label>
              <Form.Select
                value={leadConvertStatusSelectKey}
                onChange={(e) =>
                  typeof setLeadConvertStatusSelectKey === 'function' &&
                  setLeadConvertStatusSelectKey(e.target.value)
                }
                disabled={
                  leadStatusPicklistForConvertHide.length === 0 ||
                  typeof setLeadConvertStatusSelectKey !== 'function'
                }
              >
                <option value="">
                  {leadStatusPicklistForConvertHide.length === 0
                    ? '— No Lead Status options (check field is picklist) —'
                    : '— Not set —'}
                </option>
                {(Array.isArray(leadStatusPicklistForConvertHide) ? leadStatusPicklistForConvertHide : []).map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.value || o.key}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                On successful conversion, the lead&apos;s <code>lead_status</code> is set to this value. On the lead detail
                page, Convert Lead stays hidden while the record is already in this status (key and label are both matched).
                Choose &quot;Not set&quot; if you do not want to change status on convert.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="field-mapping-container">
        {/* Header Row */}
        <div className="mapping-header-row">
          <div className="mapping-col">
            <h5 className="mb-0">Lead Fields</h5>
          </div>
          {enabledTargets.map(target => (
            <div key={target.key} className="mapping-col">
              <h5 className="mb-0">{target.label}</h5>
            </div>
          ))}
        </div>

        {/* Scrollable Content */}
        <div className="mapping-content" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {loadingMappingFields ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading fields...</span>
              </div>
              <div className="mt-2 text-muted">Loading mapping fields...</div>
            </div>
          ) : !hasEnabledTargets ? (
            <div className="text-muted text-center py-4">
              No target objects selected. Enable at least one object to map fields.
            </div>
          ) : fields && fields.length > 0 ? (
            fields
              .filter((field) => !isLeadFieldExcludedFromMapping(field, { allowIdField: dontMarkConverted }))
              .map((field) => (
                <div key={field.id} className="mapping-row">
                  {/* Lead Field Column */}
                  <div className="mapping-col pt-3">
                    <div className="field-item p-2 border rounded mb-2 bg-light">
                      <div className="field-name fw-bold">{field.field_label}</div>
                      {/*
                          <div className="field-api-name text-muted small">
                            <code>{field.field_name}</code>
                          </div>
                          <div className="field-type text-muted small">{field.data_type}</div>
                        */}
                    </div>
                  </div>

                  {enabledTargets.map(target => {
                    const targetObjectId = objectIds[target.key];
                    const isTargetReady = Boolean(targetObjectId);
                    const skipNameMapping = isStandardConversionSkipsLeadNameMapping(field, target.key);
                    const currentMapping = isTargetReady && !skipNameMapping
                      ? getCurrentMapping(field.id, targetObjectId)
                      : '';
                    const availableFields = isTargetReady && !skipNameMapping
                      ? getAvailableFields(field.id, targetObjectId, target.fields)
                      : [];
                    const isCompleted = isTargetReady && !skipNameMapping
                      ? isMappingCompleted(field.id, targetObjectId)
                      : false;

                    return (
                      <div key={target.key} className="mapping-col pt-2">
                        <div className={`mapping-item p-2 border rounded mb-2 ${isCompleted ? 'bg-warning bg-opacity-25' : ''}`}>
                          {skipNameMapping ? (
                            <div className="text-muted small py-1 px-2">
                              Handled by standard conversion
                            </div>
                          ) : (
                            <select
                              className="form-select form-select-sm"
                              value={currentMapping}
                              onChange={(e) => handleMappingChange(field.id, targetObjectId, e.target.value)}
                              disabled={!isTargetReady}
                            >
                              <option value="">{isTargetReady ? '-Select-' : 'Loading...'}</option>
                              {availableFields.map((targetField) => (
                                <option key={targetField.id} value={targetField.id}>
                                  {targetField.field_label} ({targetField.data_type})
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
          ) : (
            <div className="text-muted text-center py-4">No fields available</div>
          )}
        </div>
      </div>
    </>
  );

  const footerContent = (
    <>
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
      {allowMigrationConfig && (
        <Button
          variant="outline-secondary"
          onClick={handleOpenMigrationConfig}
          disabled={savingMappings}
        >
          Config Migration
        </Button>
      )}
      <Button
        variant="primary"
        onClick={saveFieldMappings}
        disabled={savingMappings}
      >
        {savingMappings ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Saving...
          </>
        ) : (
          'Save Mapping'
        )}
      </Button>
    </>
  );

  if (variant === 'page') {
    return (
      <div className="field-mapping-page mb-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            {headerContent}
          </div>
          <div className="d-flex gap-2">
            {allowMigrationConfig && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleOpenMigrationConfig}
                disabled={savingMappings}
              >
                Config Migration
              </Button>
            )}
            <Button variant="outline-secondary" size="sm" onClick={onClose}>
              ← Back to Fields
            </Button>
          </div>
        </div>
        {bodyContent}
        <div className="d-flex justify-content-end gap-2 mt-3">
          {footerContent}
        </div>
        {allowMigrationConfig && (
          <Modal
            show={showMigrationConfigModal}
            onHide={() => setShowMigrationConfigModal(false)}
            size="lg"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Config Migration</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {migrationConfigLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2">Loading configuration...</p>
                </div>
              ) : (
                <div className="border rounded bg-light p-3">
                  {childObjectOptions.map((child) => (
                    <Row key={child.key} className="align-items-center mb-2">
                      <Col md={5}>
                        <span>{child.label}</span>
                      </Col>
                      <Col md={7}>
                        <Form.Select
                          value={resolveMigrationMappingValue(child.key)}
                          onChange={(e) => handleMigrationConfigChange(child.key, e.target.value)}
                        >
                          <option value="skip">Skip</option>
                          {resolveAllowedTargets(child.key).map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                    </Row>
                  ))}
                  <Form.Text className="text-muted">
                    Set the default migration target for each related record type.
                  </Form.Text>
                </div>
              )}
              {migrationConfigError && (
                <Alert variant="danger" className="mt-3">
                  {migrationConfigError}
                </Alert>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowMigrationConfigModal(false)}
                disabled={migrationConfigLoading}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveMigrationConfig}
                disabled={migrationConfigLoading}
              >
                {migrationConfigLoading ? 'Saving...' : 'Save'}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{headerContent}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {bodyContent}
      </Modal.Body>
      <Modal.Footer>
        {footerContent}
      </Modal.Footer>
      {allowMigrationConfig && (
        <Modal
          show={showMigrationConfigModal}
          onHide={() => setShowMigrationConfigModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Config Migration</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {migrationConfigLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading configuration...</p>
              </div>
            ) : (
              <div className="border rounded bg-light p-3">
                {childObjectOptions.map((child) => (
                  <Row key={child.key} className="align-items-center mb-2">
                    <Col md={5}>
                      <span>{child.label}</span>
                    </Col>
                    <Col md={7}>
                      <Form.Select
                        value={resolveMigrationMappingValue(child.key)}
                        onChange={(e) => handleMigrationConfigChange(child.key, e.target.value)}
                      >
                        <option value="skip">Skip</option>
                        {resolveAllowedTargets(child.key).map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>
                ))}
                <Form.Text className="text-muted">
                  Set the default migration target for each related record type.
                </Form.Text>
              </div>
            )}
            {migrationConfigError && (
              <Alert variant="danger" className="mt-3">
                {migrationConfigError}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowMigrationConfigModal(false)}
              disabled={migrationConfigLoading}
            >
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveMigrationConfig}
              disabled={migrationConfigLoading}
            >
              {migrationConfigLoading ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Modal>
  );
};

export default FieldMappingManager;
